import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  TextField,
} from "@mui/material";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import CustomButton from "../../../../components/common/button/CustomButton";

interface PercentageRule {
  assignTo: string;
  percentage: number;
}

const USERS = ["User A", "User B", "User C", "User D"];

const PercentageSplitAllocation = () => {
  const [rules, setRules] = useState<PercentageRule[]>([
    { assignTo: "User A", percentage: 40 },
    { assignTo: "User B", percentage: 30 },
    { assignTo: "User C", percentage: 30 },
  ]);

  const total = rules.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
  const isValid = total === 100;

  const handleChange = (index: number, field: string, value: any) => {
    setRules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDelete = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setRules((prev) => [...prev, { assignTo: "User A", percentage: 0 }]);
  };

  return (
    <Paper sx={{ padding: "16px", borderRadius: "8px" }}>
      {/* Header */}
      <Box sx={{ marginBottom: "12px" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333", fontSize: "14px", marginBottom: "4px" }}>
          Percentage Split Allocation
        </Typography>
        <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
          Automatically distribute leads based on percentage split.
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "16px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {["Assign To", "Percentage (%)", "Action"].map((h, i) => (
                <TableCell
                  key={h}
                  align={i === 2 ? "center" : "left"}
                  sx={{ fontWeight: 600, color: "#333", fontSize: "12px", padding: "6px 8px" }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, i) => (
              <TableRow key={i} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                {/* Assign To */}
                <TableCell sx={{ padding: "6px 8px" }}>
                  <Select
                    value={rule.assignTo}
                    onChange={(e) => handleChange(i, "assignTo", e.target.value)}
                    size="small"
                    sx={{ minWidth: "110px", fontSize: "12px", height: "28px" }}
                  >
                    {USERS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </Select>
                </TableCell>

                {/* Percentage */}
                <TableCell sx={{ padding: "6px 8px" }}>
                  <TextField
                    type="number"
                    value={rule.percentage}
                    onChange={(e) => handleChange(i, "percentage", Number(e.target.value))}
                    size="small"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: "90px", "& input": { fontSize: "12px", padding: "4px 8px" } }}
                  />
                </TableCell>

                {/* Action */}
                <TableCell align="center" sx={{ padding: "6px 8px" }}>
                  <Tooltip title="Delete rule">
                    <IconButton size="small" onClick={() => handleDelete(i)} sx={{ padding: "2px" }}>
                      <MoreIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {/* Total row */}
            <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
              <TableCell sx={{ padding: "6px 8px", fontWeight: 700, fontSize: "12px" }}>
                Total
              </TableCell>
              <TableCell
                sx={{
                  padding: "6px 8px",
                  fontWeight: 700,
                  fontSize: "12px",
                  color: isValid ? "#02906f" : "#e53935",
                }}
              >
                {total}% {!isValid && `(needs ${100 - total > 0 ? `+${100 - total}` : 100 - total}% more)`}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <CustomButton title="+ Add Rule" onClick={handleAdd} />

      {/* Hint */}
      <Typography variant="caption" sx={{ display: "block", color: "#999", fontSize: "11px", marginTop: "10px", marginBottom: "10px" }}>
        Percentage split is the final fallback when no other rules match.
      </Typography>

      <Divider sx={{ marginBottom: "12px" }} />

      {/* Footer */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: isValid ? "#02906f" : "#e53935",
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, fontSize: "13px", color: isValid ? "#02906f" : "#e53935" }}
          >
            {isValid ? "Rules Valid - All set for now!" : `Total must equal 100% (currently ${total}%)`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <CustomButton title="Reset Draft" variant="outlined" onClick={() => {}} />
          <CustomButton title="Save" onClick={() => {}} />
          <CustomButton title="Save & Activate" onClick={() => {}} />
        </Box>
      </Box>
    </Paper>
  );
};

export default PercentageSplitAllocation;